import { InitializeMode, PersistenceLevel, Store } from '@/core/flux.service';
import {
  AgentType,
  AnswerStyle,
  ChatAttachment,
  ChatMessage,
  FontSize,
  ReasoningStyle,
  SelectedDocument,
  AIMemory,
} from '@/aiAssistant/aiAssistant.types';
import _ from 'lodash';

interface AIAssistantState {
  displayed: boolean;
  maximized: boolean;
  messages: ChatMessage[];
  selectedAgent: AgentType;
  fontSize: FontSize;
  externalPrompt: { prompt: string; agent?: AgentType; insertOnly?: boolean } | undefined;
  answerStyle: AnswerStyle;
  reasoningStyle: ReasoningStyle;
  refreshHistory: boolean;
  activeChat: { id: string; name: string } | undefined;
  historySearchValue: string;
  selectedDocument: SelectedDocument | SelectedDocument[] | null;
  isProcessingExternalInput: 'running' | 'completed' | 'stopped';
  generateMemory: boolean;
  savedMemories: AIMemory[];
  preferredSubsection: 'general' | 'memories' | 'privacy' | null;
  draftAttachments: ChatAttachment[];
}

export class AiAssistantStore extends Store {
  static readonly storeName = 'sqAiAssistantStore';
  persistenceLevel: PersistenceLevel = 'WORKBENCH';

  initialize(initializeMode: InitializeMode) {
    this.state = this.immutable({
      displayed: false,
      lastSubmittedPrompt: '',
      maximized: false,
      fontSize: 'small',
      answerStyle: 'moderate',
      reasoningStyle: 'enabled',
      refreshHistory: true,
      activeChat: undefined,
      historySearchValue: '',
      isProcessingExternalInput: 'stopped',
      selectedDocument: null,
      generateMemory: true,
      savedMemories: [],
      preferredSubsection: null,
      draftAttachments: [],
    });
  }

  get displayed() {
    return this.state.get('displayed');
  }

  get maximized() {
    return this.state.get('maximized');
  }

  get messages() {
    return this.state.get('messages') || [];
  }

  get selectedAgent() {
    return this.state.get('selectedAgent');
  }

  get fontSize() {
    return this.state.get('fontSize') || 'small';
  }

  get externalPrompt() {
    return this.state.get('externalPrompt');
  }

  get answerStyle() {
    return this.state.get('answerStyle');
  }

  get reasoningStyle() {
    return this.state.get('reasoningStyle');
  }

  get refreshHistory() {
    return this.state.get('refreshHistory');
  }

  get activeChat() {
    return this.state.get('activeChat');
  }

  get historySearchValue() {
    return this.state.get('historySearchValue') || '';
  }

  get isProcessingExternalInput() {
    return this.state.get('isProcessingExternalInput');
  }

  get selectedDocument() {
    return this.state.get('selectedDocument');
  }

  get generateMemory() {
    return this.state.get('generateMemory');
  }

  get savedMemories() {
    return this.state.get('savedMemories') || [];
  }

  get preferredSubsection() {
    return this.state.get('preferredSubsection');
  }

  get draftAttachments() {
    return this.state.get('draftAttachments') || [];
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate() {
    return _.omit(this.state.serialize(), [
      'messages',
      'externalPrompt',
      'historySearchValue',
      'selectedDocument',
      'preferredSubsection',
      'draftAttachments',
    ]);
  }

  /**
   * Re-creates the AI Store. All necessary data needed to rehydrate is persisted so no actions have to be
   * called.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: AIAssistantState) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    /**
     * Sets the displayed flag
     */
    AI_ASSISTANT_DISPLAYED: (payload: { displayed: boolean }) => {
      this.state.set('displayed', payload.displayed);
    },

    /**
     * Sets the maximized flag
     */
    AI_ASSISTANT_MAXIMIZED: (payload: { maximized: boolean }) => {
      this.state.set('maximized', payload.maximized);
    },

    AI_ASSISTANT_SET_MESSAGES: (payload: { messages: ChatMessage[] }) => {
      this.state.set('messages', payload.messages);
    },

    AI_ASSISTANT_SET_SELECTED_AGENT: (payload: { agent: AgentType }) => {
      this.state.set('selectedAgent', payload.agent);
    },

    AI_ASSISTANT_SET_FONT_SIZE: (payload: { fontSize: FontSize }) => {
      this.state.set('fontSize', payload.fontSize);
    },

    AI_ASSISTANT_EXTERNAL_PROMPT: (
      payload: { prompt: string; agent?: AgentType; insertOnly?: boolean } | undefined,
    ) => {
      this.state.set('externalPrompt', payload);
    },

    AI_ASSISTANT_SET_ANSWER_STYLE: (payload: { answerStyle: AnswerStyle }) => {
      this.state.set('answerStyle', payload.answerStyle);
    },

    AI_ASSISTANT_SET_REASONING_STYLE: (payload: { reasoningStyle: ReasoningStyle }) => {
      this.state.set('reasoningStyle', payload.reasoningStyle);
    },

    AI_ASSISTANT_SET_REFRESH_HISTORY: (payload: { refreshHistory: boolean }) => {
      this.state.set('refreshHistory', payload.refreshHistory);
    },

    AI_ASSISTANT_SET_ACTIVE_CHAT: (payload: { chat: { id: string; name: string } | undefined }) => {
      this.state.set('activeChat', payload.chat);
    },

    AI_ASSISTANT_SET_HISTORY_SEARCH: (payload: { searchValue: string }) => {
      this.state.set('historySearchValue', payload.searchValue);
    },

    AI_ASSISTANT_SET_PROCESSING_EXTERNAL_INPUT: (payload: { value: 'running' | 'completed' | 'stopped' }) => {
      this.state.set('isProcessingExternalInput', payload.value);
    },

    AI_ASSISTANT_SET_SELECTED_DOCUMENT: (payload: { document: SelectedDocument | SelectedDocument[] | null }) => {
      this.state.set('selectedDocument', payload.document);
    },

    AI_ASSISTANT_SET_GENERATE_MEMORY: (payload: { generateMemory: boolean }) => {
      this.state.set('generateMemory', payload.generateMemory);
    },

    AI_ASSISTANT_SET_SAVED_MEMORIES: (payload: { savedMemories: AIMemory[] }) => {
      this.state.set('savedMemories', payload.savedMemories);
    },

    AI_ASSISTANT_SET_PREFERRED_SUBSECTION: (payload: { subsection: 'general' | 'memories' | 'privacy' | null }) => {
      this.state.set('preferredSubsection', payload.subsection);
    },

    AI_ASSISTANT_ADD_MEMORY: (payload: { memory: AIMemory }) => {
      const currentMemories = this.state.get('savedMemories') || [];
      this.state.set('savedMemories', [...currentMemories, payload.memory]);
    },

    AI_ASSISTANT_REMOVE_MEMORY: (payload: { memoryId: string }) => {
      const currentMemories = this.state.get('savedMemories') || [];
      const filteredMemories = currentMemories.filter((memory: AIMemory) => memory.id !== payload.memoryId);
      this.state.set('savedMemories', filteredMemories);
    },

    AI_ASSISTANT_UPDATE_MEMORY: (payload: { memory: AIMemory }) => {
      const currentMemories = this.state.get('savedMemories') || [];
      const updatedMemories = currentMemories.map((memory: AIMemory) =>
        memory.id === payload.memory.id ? payload.memory : memory,
      );
      this.state.set('savedMemories', updatedMemories);
    },

    AI_ASSISTANT_ADD_DRAFT_ATTACHMENT: (payload: { attachment: ChatAttachment }) => {
      const currentAttachments = this.state.get('draftAttachments') || [];
      this.state.set('draftAttachments', [...currentAttachments, payload.attachment]);
    },

    AI_ASSISTANT_REMOVE_DRAFT_ATTACHMENT: (payload: { attachmentId: string }) => {
      const currentAttachments = this.state.get('draftAttachments') || [];
      const filteredAttachments = currentAttachments.filter(
        (attachment: ChatAttachment) => attachment.id !== payload.attachmentId,
      );
      this.state.set('draftAttachments', filteredAttachments);
    },

    AI_ASSISTANT_CLEAR_DRAFT_ATTACHMENTS: () => {
      this.state.set('draftAttachments', []);
    },
  };
}
